import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Row, Col, Card, Form, Button, Table, Spinner, Badge, Alert, ButtonGroup } from 'react-bootstrap';
import {
    Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    Line, ComposedChart
} from 'recharts';
import {
    Filter, TrendingUp, Clock, CheckCircle2, Plane, RefreshCw, ShieldAlert, Sparkles, Moon
} from 'lucide-react';
import { getEvaAiInsights } from '../api';
import './EvaAiInsightsPanel.css';

const getDefaultStart30Days = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
};

const getTodayIso = () => new Date().toISOString().split('T')[0];

const STATUS_BADGE_MAP = {
    completed: { bg: 'success', label: 'Completada' },
    on_board: { bg: 'primary', label: 'A Bordo' },
    arrived: { bg: 'info', label: 'En Punto' },
    en_route: { bg: 'info', label: 'En Ruta' },
    confirmed: { bg: 'primary', label: 'Confirmada' },
    assigned: { bg: 'info', label: 'Asignada' },
    pending: { bg: 'warning', text: 'dark', label: 'Por Despachar' },
    draft: { bg: 'secondary', label: 'Pre-Reserva / Sin Pago' },
    cancelled: { bg: 'danger', label: 'Cancelada' }
};

const EvaAiInsightsPanel = ({ bases = [] }) => {
    const [filters, setFilters] = useState({
        start_date: getDefaultStart30Days(),
        end_date: getTodayIso(),
        base_id: '',
        date_field: 'created_at'
    });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [bucketFilter, setBucketFilter] = useState('active');
    const [searchTerm, setSearchTerm] = useState('');

    const fetchEvaInsights = useCallback(async (overrideFilters = null) => {
        const activeF = overrideFilters || filters;
        setLoading(true);
        setError('');
        try {
            const params = {
                start_date: activeF.start_date,
                end_date: activeF.end_date,
                date_field: activeF.date_field || 'created_at'
            };
            if (activeF.base_id) {
                params.base_id = activeF.base_id;
            }
            const res = await getEvaAiInsights(params);
            setData(res);
            // Si en el rango seleccionado no hay efectivas pero sí borradores/canceladas, mostrar todas automáticamente
            if (res?.summary && res.summary.active_count === 0 && res.summary.total_generated > 0) {
                setBucketFilter('all');
            }
        } catch (err) {
            console.error('Error fetching Eva AI insights:', err);
            setError(err.response?.data?.detail || 'Error al cargar el panel de Rendimiento Eva (IA).');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchEvaInsights();
    }, [fetchEvaInsights]);

    const applyPresetRange = (preset) => {
        const now = new Date();
        let nextStart = filters.start_date;
        let nextEnd = getTodayIso();

        if (preset === 'current_month') {
            nextStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
            if (filters.date_field === 'scheduled_time') {
                nextEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
            }
        } else if (preset === 'prev_month') {
            nextStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
            nextEnd = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
        } else if (preset === 'last_30') {
            nextStart = getDefaultStart30Days();
        } else if (preset === 'all_history') {
            nextStart = '2026-09-01';
            if (filters.date_field === 'scheduled_time') {
                const future = new Date(now.getFullYear(), now.getMonth() + 4, 0);
                nextEnd = future.toISOString().split('T')[0];
            }
        }

        const updated = { ...filters, start_date: nextStart, end_date: nextEnd };
        setFilters(updated);
    };

    const filteredTrips = useMemo(() => {
        if (!data?.trips) return [];
        const q = searchTerm.trim().toLowerCase();
        return data.trips.filter((t) => {
            if (bucketFilter !== 'all' && t.bucket !== bucketFilter) {
                return false;
            }
            if (!q) return true;
            return (
                String(t.id).includes(q)
                || (t.client_name || '').toLowerCase().includes(q)
                || (t.passenger_name || '').toLowerCase().includes(q)
                || (t.passenger_phone || '').toLowerCase().includes(q)
                || (t.pickup_address || '').toLowerCase().includes(q)
                || (t.dropoff_address || '').toLowerCase().includes(q)
                || (t.driver_name || '').toLowerCase().includes(q)
                || (t.dispatched_by || '').toLowerCase().includes(q)
            );
        });
    }, [data, bucketFilter, searchTerm]);

    const summary = data?.summary || {};
    const ops = data?.operational_actions || {};

    return (
        <div>
            {/* BARRA DE FILTROS */}
            <Card className="mb-4 shadow-sm border-0">
                <Card.Body className="p-3 bg-light rounded">
                    <Row className="align-items-end g-3">
                        <Col xl={2} lg={3} md={4} sm={6}>
                            <Form.Label htmlFor="eva-filter-date-field" className="small fw-bold text-muted text-uppercase">
                                Criterio Fecha
                            </Form.Label>
                            <Form.Select
                                id="eva-filter-date-field"
                                size="sm"
                                value={filters.date_field}
                                onChange={(e) => setFilters(prev => ({ ...prev, date_field: e.target.value }))}
                            >
                                <option value="created_at">💬 Fecha Captación (Chat)</option>
                                <option value="scheduled_time">🗓️ Fecha del Servicio</option>
                            </Form.Select>
                        </Col>
                        <Col xl={2} lg={2} md={4} sm={6}>
                            <Form.Label htmlFor="eva-filter-start-date" className="small fw-bold text-muted text-uppercase">
                                Desde
                            </Form.Label>
                            <Form.Control
                                id="eva-filter-start-date"
                                type="date"
                                size="sm"
                                value={filters.start_date}
                                onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                            />
                        </Col>
                        <Col xl={2} lg={2} md={4} sm={6}>
                            <Form.Label htmlFor="eva-filter-end-date" className="small fw-bold text-muted text-uppercase">
                                Hasta
                            </Form.Label>
                            <Form.Control
                                id="eva-filter-end-date"
                                type="date"
                                size="sm"
                                value={filters.end_date}
                                onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                            />
                        </Col>
                        <Col xl={2} lg={2} md={4} sm={6}>
                            <Form.Label htmlFor="eva-filter-base" className="small fw-bold text-muted text-uppercase">
                                Base / Ciudad
                            </Form.Label>
                            <Form.Select
                                id="eva-filter-base"
                                size="sm"
                                value={filters.base_id}
                                onChange={(e) => setFilters(prev => ({ ...prev, base_id: e.target.value }))}
                            >
                                <option value="">🏢 Todas las Bases</option>
                                {bases.map((b) => (
                                    <option key={b.id} value={b.id}>
                                        {b.name} ({b.country_code})
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xl={4} lg={3} md={8} sm={12} className="d-flex flex-wrap gap-2">
                            <Button
                                variant="primary"
                                size="sm"
                                className="fw-bold px-3 eva-focus-ring"
                                style={{ backgroundColor: '#7c3aed', borderColor: '#6d28d9' }}
                                onClick={() => fetchEvaInsights()}
                                disabled={loading}
                            >
                                {loading ? (
                                    <Spinner size="sm" animation="border" />
                                ) : (
                                    <>
                                        <Filter size={14} className="me-1" aria-hidden="true" /> Actualizar
                                    </>
                                )}
                            </Button>
                            <ButtonGroup size="sm" aria-label="Rangos rápidos de fecha">
                                <Button variant="outline-secondary" onClick={() => applyPresetRange('last_30')}>
                                    30 Días
                                </Button>
                                <Button variant="outline-secondary" onClick={() => applyPresetRange('current_month')}>
                                    Mes
                                </Button>
                                <Button variant="outline-secondary" onClick={() => applyPresetRange('prev_month')}>
                                    Mes Ant.
                                </Button>
                                <Button variant="outline-secondary" onClick={() => applyPresetRange('all_history')}>
                                    Histórico
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {error && <Alert variant="danger">{error}</Alert>}

            {loading && !data ? (
                <div className="text-center p-5">
                    <Spinner animation="border" variant="primary" />
                    <h5 className="mt-3">Analizando captación y ahorro operativo de Eva (IA)...</h5>
                </div>
            ) : data && (
                <>
                    {/* FILA 1: EMBUDO EJECUTIVO DE RESERVAS IA */}
                    <Row className="g-3 mb-4">
                        <Col xl={3} md={6}>
                            <Card className="shadow-sm border-0 bg-white h-100 border-start border-4" style={{ borderColor: '#7c3aed' }}>
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <div className="text-muted small fw-bold text-uppercase">
                                                Reservas Efectivas (IA)
                                            </div>
                                            <h3 className="fw-bold mb-1 mt-1" style={{ color: '#7c3aed' }}>
                                                {(summary.active_count || 0).toLocaleString('es-CL')}
                                            </h3>
                                            <small className="text-dark fw-semibold d-block">
                                                {summary.convenio_active_count || 0} Convenio · {summary.particular_active_count || 0} Particular pagada
                                            </small>
                                            <small className="text-muted d-block mt-1" style={{ fontSize: '11px' }}>
                                                {summary.completed_count || 0} finalizadas · {summary.assigned_count || 0} con chofer · {summary.pending_driver_count || 0} por asignar
                                            </small>
                                        </div>
                                        <div
                                            className="p-2 rounded-circle d-flex align-items-center justify-content-center"
                                            style={{ backgroundColor: 'rgba(124, 58, 237, 0.12)', color: '#7c3aed' }}
                                            aria-hidden="true"
                                        >
                                            <Sparkles size={22} aria-hidden="true" />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xl={3} md={6}>
                            <Card className="shadow-sm border-0 bg-white h-100 border-start border-4 border-success">
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <div className="text-muted small fw-bold text-uppercase">
                                                Venta Efectiva Generada
                                            </div>
                                            <h3 className="fw-bold mb-1 mt-1 text-success">
                                                ${(summary.active_sales_clp || 0).toLocaleString('es-CL')}
                                            </h3>
                                            <small className="text-dark fw-semibold d-block">
                                                Margen est.: ${(summary.active_margin_clp || 0).toLocaleString('es-CL')}
                                            </small>
                                            <small className="text-muted d-block mt-1" style={{ fontSize: '11px' }}>
                                                {summary.unique_active_clients || 0} cliente(s)/convenios activos
                                            </small>
                                        </div>
                                        <div className="p-2 bg-success bg-opacity-10 rounded-circle text-success" aria-hidden="true">
                                            <TrendingUp size={22} aria-hidden="true" />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xl={3} md={6}>
                            <Card className="shadow-sm border-0 bg-white h-100 border-start border-4 border-info">
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <div className="text-muted small fw-bold text-uppercase">
                                                Captación Horario 24/7
                                            </div>
                                            <h3 className="fw-bold mb-1 mt-1 text-info">
                                                {summary.off_hours_active_count || 0}{' '}
                                                <span className="fs-6 fw-normal text-muted">
                                                    ({summary.off_hours_active_pct || 0}%)
                                                </span>
                                            </h3>
                                            <small className="text-dark fw-semibold d-block">
                                                Noches (20:00–08:00) y Fines de Semana
                                            </small>
                                            <small className="text-muted d-block mt-1" style={{ fontSize: '11px' }}>
                                                {summary.off_hours_total_count || 0} solicitudes totales fuera de horario hábil
                                            </small>
                                        </div>
                                        <div className="p-2 bg-info bg-opacity-10 rounded-circle text-info" aria-hidden="true">
                                            <Moon size={22} aria-hidden="true" />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xl={3} md={6}>
                            <Card className="shadow-sm border-0 bg-white h-100 border-start border-4 border-warning">
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <div className="text-muted small fw-bold text-uppercase">
                                                Embudo Total Creado
                                            </div>
                                            <h3 className="fw-bold mb-1 mt-1 text-dark">
                                                {(summary.total_generated || 0).toLocaleString('es-CL')}
                                            </h3>
                                            <small className="text-dark fw-semibold d-block">
                                                {summary.draft_count || 0} en borrador (${(summary.draft_potential_sales_clp || 0).toLocaleString('es-CL')})
                                            </small>
                                            <small className="text-muted d-block mt-1" style={{ fontSize: '11px' }}>
                                                {summary.cancelled_count || 0} canceladas ({summary.cancelled_unpaid_count || 0} links dLocal expirados/prueba)
                                            </small>
                                        </div>
                                        <div className="p-2 bg-warning bg-opacity-15 rounded-circle text-warning" aria-hidden="true">
                                            <Clock size={22} aria-hidden="true" />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* FILA 2: BANDA DE AHORRO OPERATIVO AUTÓNOMO EN WHATSAPP */}
                    <Card
                        className="shadow-sm border-0 mb-4 overflow-hidden"
                        style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)', color: '#ffffff' }}
                    >
                        <Card.Body className="p-4">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                                <div>
                                    <h5 className="fw-bold mb-1 text-white d-flex align-items-center gap-2">
                                        ⚡ Ahorro Operativo Autónomo en WhatsApp
                                    </h5>
                                    <small className="text-light opacity-75">
                                        Gestiones resueltas directamente por la IA en el período sin intervención telefónica ni manual de Locución.
                                    </small>
                                </div>
                                <Badge bg="warning" text="dark" className="px-3 py-2 fw-bold fs-6 shadow-sm">
                                    ⏱️ ~{ops.estimated_hours_saved || 0} hrs operativas ahorradas ({ops.estimated_minutes_saved || 0} min)
                                </Badge>
                            </div>

                            <Row className="g-3">
                                <Col xl={3} md={6}>
                                    <div className="p-3 rounded h-100" style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.14)' }}>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span className="small text-uppercase fw-bold text-light opacity-75">Confirmaciones Recordatorio</span>
                                            <CheckCircle2 size={18} className="text-success" aria-hidden="true" />
                                        </div>
                                        <div className="fs-4 fw-bold text-white">
                                            {(ops.reminder_confirmations || 0).toLocaleString('es-CL')}
                                        </div>
                                        <small className="text-light opacity-75" style={{ fontSize: '11px' }}>
                                            Pasajeros confirmados por recordatorio IA
                                            {ops.interactive_button_confirmations > 0 ? ` (+${ops.interactive_button_confirmations} botones WA)` : ''}
                                        </small>
                                    </div>
                                </Col>

                                <Col xl={3} md={6}>
                                    <div className="p-3 rounded h-100" style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.14)' }}>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span className="small text-uppercase fw-bold text-light opacity-75">Vuelos Registrados / Validados</span>
                                            <Plane size={18} className="text-info" aria-hidden="true" />
                                        </div>
                                        <div className="fs-4 fw-bold text-white">
                                            {(ops.flight_registrations || 0).toLocaleString('es-CL')}
                                        </div>
                                        <small className="text-light opacity-75" style={{ fontSize: '11px' }}>
                                            Números de vuelo capturados y guardados en el viaje
                                        </small>
                                    </div>
                                </Col>

                                <Col xl={3} md={6}>
                                    <div className="p-3 rounded h-100" style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.14)' }}>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span className="small text-uppercase fw-bold text-light opacity-75">Cambios & Duplicados Resueltos</span>
                                            <RefreshCw size={18} className="text-warning" aria-hidden="true" />
                                        </div>
                                        <div className="fs-4 fw-bold text-white">
                                            {(
                                                (ops.autonomous_modifications || 0)
                                                + (ops.autonomous_cancellations || 0)
                                                + (ops.duplicates_prevented || 0)
                                            ).toLocaleString('es-CL')}
                                        </div>
                                        <small className="text-light opacity-75" style={{ fontSize: '11px' }}>
                                            {ops.autonomous_modifications || 0} reprogramaciones · {ops.autonomous_cancellations || 0} canc. (&gt;12h) · {ops.duplicates_prevented || 0} duplicados evitados
                                        </small>
                                    </div>
                                </Col>

                                <Col xl={3} md={6}>
                                    <div className="p-3 rounded h-100" style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.14)' }}>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span className="small text-uppercase fw-bold text-light opacity-75">Protección SLA (&lt;24h / En Ruta)</span>
                                            <ShieldAlert size={18} style={{ color: '#f87171' }} aria-hidden="true" />
                                        </div>
                                        <div className="fs-4 fw-bold text-white">
                                            {(ops.sla_protections || 0).toLocaleString('es-CL')}
                                        </div>
                                        <small className="text-light opacity-75" style={{ fontSize: '11px' }}>
                                            Cambios fuera de plazo contenidos y alertados a Despacho
                                        </small>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* FILA 3: EVOLUCIÓN DIARIA Y CLIENTES ATENDIDOS */}
                    <Row className="g-4 mb-4 eva-containment-region">
                        <Col lg={7}>
                            <Card className="shadow-sm border-0 h-100">
                                <Card.Header className="bg-white py-3 border-bottom">
                                    <h6 className="fw-bold mb-0 text-dark">
                                        📊 Evolución de Captación IA ({filters.date_field === 'scheduled_time' ? 'Por Fecha de Viaje' : 'Por Fecha de Chat'})
                                    </h6>
                                    <small className="text-muted">
                                        Reservas efectivas vs pre-reservas sin pago y venta generada en CLP.
                                    </small>
                                </Card.Header>
                                <Card.Body>
                                    {data.daily_trend && data.daily_trend.length > 0 ? (
                                        <div style={{ width: '100%', height: 270 }}>
                                            <ResponsiveContainer>
                                                <ComposedChart data={data.daily_trend} margin={{ top: 10, right: 15, left: 0, bottom: 0 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                                    <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11 }} />
                                                    <YAxis
                                                        yAxisId="right"
                                                        orientation="right"
                                                        tickFormatter={(v) => `$${Math.round(v / 1000)}k`}
                                                        tick={{ fontSize: 11 }}
                                                    />
                                                    <Tooltip
                                                        formatter={(value, name) => {
                                                            if (name === 'Venta Efectiva') {
                                                                return [`$${Number(value || 0).toLocaleString('es-CL')}`, name];
                                                            }
                                                            return [value, name];
                                                        }}
                                                    />
                                                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                                                    <Bar yAxisId="left" dataKey="active" name="Efectivas" stackId="a" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                                                    <Bar yAxisId="left" dataKey="draft" name="Sin Pago / Borrador" stackId="a" fill="#94a3b8" />
                                                    <Line yAxisId="right" type="monotone" dataKey="sales" name="Venta Efectiva" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} />
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        </div>
                                    ) : (
                                        <div className="text-center text-muted py-5 small">
                                            Sin actividad de creación de reservas en este rango de fechas. Prueba con el botón <strong>30 Días</strong> o <strong>Histórico</strong>.
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={5}>
                            <Card className="shadow-sm border-0 h-100">
                                <Card.Header className="bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="fw-bold mb-0 text-dark">🏢 Clientes y Convenios en Eva</h6>
                                        <small className="text-muted">Desglose de uso por empresa y particulares.</small>
                                    </div>
                                    <Badge bg="secondary">{data.clients_breakdown?.length || 0} clientes</Badge>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <div
                                        tabIndex={0}
                                        role="region"
                                        aria-labelledby="eva-clients-caption"
                                        style={{ maxHeight: '295px', overflowY: 'auto' }}
                                    >
                                        <Table hover size="sm" className="align-middle mb-0">
                                            <caption id="eva-clients-caption" className="visually-hidden">
                                                Desglose de reservas generadas por Eva según cliente o convenio
                                            </caption>
                                            <thead className="table-light small text-muted text-uppercase sticky-top" style={{ top: 0, zIndex: 1 }}>
                                                <tr>
                                                    <th scope="col">Cliente / Convenio</th>
                                                    <th scope="col" className="text-center">Efectivas</th>
                                                    <th scope="col" className="text-center">Borr. / Canc.</th>
                                                    <th scope="col" className="text-end">Venta Efectiva</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.clients_breakdown && data.clients_breakdown.length > 0 ? (
                                                    data.clients_breakdown.map((cl) => (
                                                        <tr key={cl.client_name}>
                                                            <th scope="row" className="fw-semibold small">
                                                                <div className="text-truncate" style={{ maxWidth: '175px' }} title={cl.client_name}>
                                                                    {cl.client_name}
                                                                </div>
                                                                <small className="text-muted fw-normal" style={{ fontSize: '10px' }}>
                                                                    {cl.segment_type}
                                                                </small>
                                                            </th>
                                                            <td className="text-center">
                                                                <Badge bg={cl.active_count > 0 ? 'success' : 'light'} text={cl.active_count > 0 ? undefined : 'dark'}>
                                                                    {cl.active_count}
                                                                </Badge>
                                                            </td>
                                                            <td className="text-center small text-muted">
                                                                {cl.draft_count} / {cl.cancelled_count}
                                                            </td>
                                                            <td className="text-end small fw-bold text-success">
                                                                ${Number(cl.sales_clp || 0).toLocaleString('es-CL')}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={4} className="text-center text-muted p-4 small">
                                                            Sin clientes registrados en este período.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* FILA 4: DETALLE DE RESERVAS GENERADAS POR EVA */}
                    <Card className="shadow-sm border-0 mb-4 eva-containment-region">
                        <Card.Header className="bg-white py-3 border-bottom">
                            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                                <div>
                                    <h5 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2">
                                        🤖 Detalle de Reservas Creadas por Eva (Autoría Permanente)
                                    </h5>
                                    <small className="text-muted">
                                        Aunque una locutora asigne o cambie el conductor después, la reserva conserva siempre la autoría de Eva y muestra quién la despachó.
                                    </small>
                                </div>
                                <div style={{ minWidth: '240px' }}>
                                    <Form.Label htmlFor="eva-trip-search" className="visually-hidden">
                                        Buscar por ID, cliente, pasajero, ruta o conductor
                                    </Form.Label>
                                    <Form.Control
                                        id="eva-trip-search"
                                        type="search"
                                        size="sm"
                                        placeholder="🔎 Buscar #ID, cliente, pasajero, chofer..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="d-flex flex-wrap gap-2 mt-3" role="group" aria-label="Filtrar reservas por estado del embudo">
                                <button
                                    type="button"
                                    className="eva-bucket-btn eva-focus-ring"
                                    aria-pressed={bucketFilter === 'active'}
                                    onClick={() => setBucketFilter('active')}
                                >
                                    ✅ Efectivas ({summary.active_count || 0})
                                </button>
                                <button
                                    type="button"
                                    className="eva-bucket-btn eva-focus-ring"
                                    aria-pressed={bucketFilter === 'draft'}
                                    onClick={() => setBucketFilter('draft')}
                                >
                                    ⏳ Sin Pago / Pre-Reserva ({summary.draft_count || 0})
                                </button>
                                <button
                                    type="button"
                                    className="eva-bucket-btn eva-focus-ring"
                                    aria-pressed={bucketFilter === 'cancelled'}
                                    onClick={() => setBucketFilter('cancelled')}
                                >
                                    🚫 Canceladas / Pruebas ({summary.cancelled_count || 0})
                                </button>
                                <button
                                    type="button"
                                    className="eva-bucket-btn eva-focus-ring"
                                    aria-pressed={bucketFilter === 'all'}
                                    onClick={() => setBucketFilter('all')}
                                >
                                    📋 Todas ({summary.total_generated || 0})
                                </button>
                            </div>
                        </Card.Header>

                        <Card.Body className="p-0">
                            <div
                                tabIndex={0}
                                role="region"
                                aria-labelledby="eva-trips-table-caption"
                                className="eva-table-wrapper"
                            >
                                <Table hover className="align-middle mb-0 eva-responsive-table" size="sm">
                                    <caption id="eva-trips-table-caption" className="visually-hidden">
                                        Listado de reservas generadas autónomamente por Asistente Eva en WhatsApp
                                    </caption>
                                    <thead className="small text-muted text-uppercase">
                                        <tr>
                                            <th scope="col" className="ps-3">#ID</th>
                                            <th scope="col">Captación (Chat)</th>
                                            <th scope="col">Fecha Servicio</th>
                                            <th scope="col">Cliente / Segmento</th>
                                            <th scope="col">Pasajero y Ruta</th>
                                            <th scope="col">Despacho Posterior</th>
                                            <th scope="col" className="text-center">Estado</th>
                                            <th scope="col" className="text-end pe-3">Venta CLP</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredTrips.length > 0 ? (
                                            filteredTrips.map((tr) => {
                                                const stMeta = STATUS_BADGE_MAP[tr.status] || { bg: 'secondary', label: tr.status };
                                                return (
                                                    <tr key={tr.id}>
                                                        <th scope="row" className="ps-3 fw-bold text-dark">
                                                            #{tr.id}
                                                        </th>
                                                        <td>
                                                            <div className="small fw-semibold">{tr.created_at_str}</div>
                                                            {tr.is_off_hours ? (
                                                                <Badge bg="dark" className="mt-1" style={{ fontSize: '10px' }}>
                                                                    🌙 Horario 24/7
                                                                </Badge>
                                                            ) : (
                                                                <small className="text-muted" style={{ fontSize: '10px' }}>Horario hábil</small>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="small fw-bold text-primary">{tr.scheduled_str}</div>
                                                            <small className="text-muted" style={{ fontSize: '11px' }}>
                                                                📍 {tr.base_name} · {tr.vehicle_type_name}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <div className="small fw-bold text-truncate" style={{ maxWidth: '175px' }} title={tr.client_name}>
                                                                {tr.client_name}
                                                            </div>
                                                            <Badge
                                                                bg={tr.is_particular ? 'info' : 'light'}
                                                                text="dark"
                                                                className="border"
                                                                style={{ fontSize: '10px' }}
                                                            >
                                                                {tr.is_particular
                                                                    ? (tr.is_paid || tr.bucket === 'active' ? '💳 dLocal Pagado' : '⏳ Link dLocal')
                                                                    : '🏢 Convenio'}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <div className="small fw-semibold">
                                                                👤 {tr.passenger_name}
                                                                {tr.flight_number ? (
                                                                    <Badge bg="light" text="dark" className="border ms-1" style={{ fontSize: '10px' }}>
                                                                        ✈️ {tr.flight_number}
                                                                    </Badge>
                                                                ) : null}
                                                            </div>
                                                            <small
                                                                className="text-muted d-block text-truncate"
                                                                style={{ maxWidth: '250px', fontSize: '11px' }}
                                                                title={`${tr.pickup_address} ➔ ${tr.dropoff_address}`}
                                                            >
                                                                {tr.pickup_address} ➔ {tr.dropoff_address}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            {tr.driver_name ? (
                                                                <>
                                                                    <div className="small fw-semibold text-dark">
                                                                        🚗 {tr.driver_name}
                                                                        {tr.vehicle_code ? ` (Móvil ${tr.vehicle_code})` : ''}
                                                                    </div>
                                                                    {tr.dispatched_by && (
                                                                        <small className="text-muted d-block" style={{ fontSize: '10px' }}>
                                                                            Asignado por: 🎧 {tr.dispatched_by}
                                                                        </small>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <span className="text-muted small">
                                                                    {tr.bucket === 'active' ? '⏳ Pendiente de asignar' : '—'}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg={stMeta.bg} text={stMeta.text} style={{ fontSize: '11px' }}>
                                                                {stMeta.label}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-end pe-3 fw-bold small">
                                                            <span className={tr.bucket === 'active' ? 'text-success' : 'text-muted'}>
                                                                ${Number(tr.price || 0).toLocaleString('es-CL')}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={8} className="text-center text-muted p-4 small">
                                                    No hay reservas en la categoría seleccionada para este filtro.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </>
            )}
        </div>
    );
};

export default EvaAiInsightsPanel;
